import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoadingController } from '@ionic/angular';
import { AuthService } from '../auth/auth.service';
import { AriaFocusFixer } from '@commons/aria-focus-fixer';

interface IUploadProgress {
  current: number;
  total: number;
  filename: string;
}

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private uploadProgress = new BehaviorSubject<IUploadProgress | null>(null);
  public uploadProgress$ = this.uploadProgress.asObservable();
  private token = '';
  private serverUrl = 'https://s3.saluddigestiva.com/api/upload';
  // serverUrl = 'http://127.0.0.1:8001/api/upload';

  constructor(private auth: AuthService, private loadingCtrl: LoadingController) {
    this.initToken();
  }

  private async initToken() {
    const user = await this.auth.getCurrentUser();
    this.token = user?.stsTokenManager?.accessToken as string;
  }

  // Convierte base64 a Blob
  base64ToBlob(base64: string, contentType: string = 'image/jpeg'): Blob {
    const byteCharacters = atob(base64);
    const byteArrays = [];
    const sliceSize = 512;

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + Math.min(sliceSize, byteCharacters.length - offset));
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
  }

  // Nuevo método para convertir URI a Blob
  async uriToBlob(uri: string): Promise<Blob> {
    const response = await fetch(uri);
    return await response.blob();
  }

  // Método para manejar múltiples cargas con progreso
  async uploadFiles(files: Array<{ file: Blob | File; filename: string }>): Promise<string[]> {
    const total = files.length;
    const urls: string[] = [];
    const loader = await this.loadingCtrl.create({
      message: `Cargando 0 de ${total} archivos`,
      backdropDismiss: false,
    });

    AriaFocusFixer.blurActiveFocus();
    await loader.present();

    try {
      for (let i = 0; i < files.length; i++) {
        const { file, filename } = files[i];

        // Actualizar progreso
        this.uploadProgress.next({
          current: i + 1,
          total,
          filename,
        });

        loader.message = `Cargando ${i + 1} de ${total} archivos`;

        const url = await this.uploadFile(file, filename);
        urls.push(url);
      }

      // Reiniciar progreso después de completar
      this.uploadProgress.next(null);

      return urls;
    } catch (error) {
      // Reiniciar progreso en caso de error
      this.uploadProgress.next(null);
      throw error;
    } finally {
      await loader.dismiss();
    }
  }

  // Método para subir un solo archivo
  async uploadFile(file: Blob | File, filename: string): Promise<string> {
    if (!file) {
      throw new Error('No se proporcionó archivo para subir');
    }

    const formData = new FormData();
    formData.append('file', file, filename);

    try {
      const response = await fetch(this.serverUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Error al subir archivo: ${response.statusText}`);
      }

      const result = await response.json();
      let fileUrl: string = result.url;

      // 🔄 CÓDIGO AÑADIDO: Reemplazar el prefijo para usar la ruta CORS
      if (fileUrl.includes('/storage/')) {
        fileUrl = fileUrl.replace('/storage/', '/reporte-image/');
      }

      return fileUrl;
    } catch (error) {
      console.error('Error en la carga del archivo:', error);
      throw error;
    }
  }

  // Método para gestionar la carga de imágenes desde la cámara
  async uploadImageFromCamera(base64Data: string, filename: string): Promise<string> {
    const blob = this.base64ToBlob(base64Data);
    return await this.uploadFile(blob, filename);
  }

  // Método para gestionar la carga de archivos genéricos
  async uploadGenericFile(file: File): Promise<string> {
    return await this.uploadFile(file, file.name);
  }

  // Método para obtener una URL firmada del servidor
  async getSignedUrl(filename: string): Promise<string> {
    const response = await fetch(`${this.serverUrl}/signed-url?filename=${filename}`, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Error al obtener URL firmada');
    }

    const data = await response.json();
    return data.url;
  }

  // Método para verificar el tipo de archivo
  validateFileType(file: File, allowedTypes: string[]): boolean {
    const fileType = file.type.split('/')[1];
    return allowedTypes.includes(fileType);
  }

  // Método para obtener el estado de la carga
  getUploadProgress(): Observable<IUploadProgress | null> {
    return this.uploadProgress$;
  }

  // Métodos de validación adicionales
  validateImageSize(file: File, maxSizeInMB: number = 5): boolean {
    const fileSizeInMB = file.size / (1024 * 1024);
    return fileSizeInMB <= maxSizeInMB;
  }

  // Método para generar nombre de archivo único
  generateUniqueFileName(originalFileName: string): string {
    const timestamp = new Date().getTime();
    const randomString = Math.random().toString(36).substring(2, 7);
    const extension = originalFileName.split('.').pop();
    return `${timestamp}_${randomString}.${extension}`;
  }
}
